import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAdminProducts, createProduct, updateProduct, deleteProduct, getAdminProduct } from '../services/api';
import './AdminProducts.css';

const CATEGORIES = ['Electronics', 'Clothing', 'Books', 'Home & Garden', 'Sports', 'Toys', 'Collectibles', 'Other'];
const CONDITIONS = ['New', 'Used - Like New', 'Used - Good', 'Used - Acceptable', 'Refurbished'];

// ── Product Form (create/edit) ────────────────────────────────────────────────
export function ProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', description: '', price: '', quantity: '', category: '',
    condition: 'New', weight_oz: '16', length_in: '12', width_in: '9', height_in: '3',
  });
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      getAdminProduct(id).then(r => {
        const p = r.data;
        setForm({
          title: p.title, description: p.description || '', price: String(p.price),
          quantity: String(p.quantity), category: p.category || '',
          condition: p.condition || 'New', weight_oz: String(p.weight_oz || 16),
          length_in: String(p.length_in || 12), width_in: String(p.width_in || 9),
          height_in: String(p.height_in || 3),
        });
        setExistingImages(p.images || []);
      });
    }
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    images.forEach(img => fd.append('images', img));

    try {
      if (isEdit) {
        await updateProduct(id, fd);
      } else {
        await createProduct(fd);
      }
      navigate('/admin/products');
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 1.5rem', maxWidth: '800px' }}>
      <div className="page-header">
        <h1>{isEdit ? 'Edit Listing' : 'List New Item'}</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 className="section-title">Item Details</h2>

          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-control" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          </div>

          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Price ($) *</label>
              <input className="form-control" type="number" step="0.01" min="0" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">Quantity *</label>
              <input className="form-control" type="number" min="0" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">Condition</label>
              <select className="form-control" value={form.condition} onChange={e => setForm({...form, condition: e.target.value})}>
                {CONDITIONS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Category</label>
            <select className="form-control" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
              <option value="">Select category...</option>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* Shipping Dimensions */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 className="section-title">📦 Package Dimensions (for shipping rates)</h2>
          <div className="form-row-4">
            <div className="form-group">
              <label className="form-label">Weight (oz)</label>
              <input className="form-control" type="number" step="0.1" value={form.weight_oz} onChange={e => setForm({...form, weight_oz: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Length (in)</label>
              <input className="form-control" type="number" step="0.1" value={form.length_in} onChange={e => setForm({...form, length_in: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Width (in)</label>
              <input className="form-control" type="number" step="0.1" value={form.width_in} onChange={e => setForm({...form, width_in: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Height (in)</label>
              <input className="form-control" type="number" step="0.1" value={form.height_in} onChange={e => setForm({...form, height_in: e.target.value})} />
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 className="section-title">📷 Photos</h2>
          {existingImages.length > 0 && (
            <div className="existing-images">
              {existingImages.map((img, i) => (
                <img key={i} src={img} alt="" className="existing-image-thumb" />
              ))}
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={e => setImages(Array.from(e.target.files))}
            className="form-control"
          />
          <p style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: '0.4rem' }}>
            Upload up to 8 photos (JPG, PNG, WEBP). Max 10MB each.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button className="btn btn-primary btn-lg" type="submit" disabled={loading}>
            {loading ? 'Saving...' : isEdit ? '💾 Save Changes' : '🚀 List Item'}
          </button>
          <Link to="/admin/products" className="btn btn-secondary btn-lg">Cancel</Link>
        </div>
      </form>
    </div>
  );
}

// ── Product List ──────────────────────────────────────────────────────────────
export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    getAdminProducts()
      .then(r => setProducts(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this listing?')) return;
    await deleteProduct(id);
    load();
  };

  return (
    <div className="container" style={{ padding: '2rem 1.5rem' }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div><h1>Listings</h1><p>Manage your items for sale.</p></div>
        <Link to="/admin/products/new" className="btn btn-primary">+ List New Item</Link>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Image</th><th>Title</th><th>Price</th><th>Views</th><th>In Carts</th><th>Saved</th><th>Conversion</th><th>Stock</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className={!p.active ? 'inactive-row' : ''}>
                  <td>
                    {p.images?.[0] ? (
                      <img src={p.images[0]} alt="" className="product-thumb" />
                    ) : (
                      <div className="product-thumb-empty">📦</div>
                    )}
                  </td>
                  <td>
                    <strong>{p.title}</strong>
                    {p.category && <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{p.category}</div>}
                  </td>
                  <td>
                    <strong>${parseFloat(p.price).toFixed(2)}</strong>
                    {p.on_sale && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--accent-dark)', marginTop: '0.2rem' }}>
                        Live at ${parseFloat(p.effective_price).toFixed(2)} ({p.sale_percent}% off)
                      </div>
                    )}
                  </td>
                  <td>
                    <strong>{p.view_count || 0}</strong>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>listing views</div>
                  </td>
                  <td>
                    <strong>{p.active_cart_count || 0}</strong>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                      {p.active_cart_units || 0} unit{Number(p.active_cart_units || 0) === 1 ? '' : 's'} held
                    </div>
                  </td>
                  <td>
                    <strong>{p.saved_count || 0}</strong>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>saved session{Number(p.saved_count || 0) === 1 ? '' : 's'}</div>
                  </td>
                  <td>
                    <strong>{p.conversion_rate || 0}%</strong>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                      {p.order_count || 0} order{Number(p.order_count || 0) === 1 ? '' : 's'} · {p.sold_units || 0} sold
                    </div>
                  </td>
                  <td>{p.quantity}</td>
                  <td>
                    <span className={`badge ${p.active ? 'badge-success' : 'badge-gray'}`}>
                      {p.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <Link to={`/admin/products/edit/${p.id}`} className="btn btn-secondary btn-sm">Edit</Link>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
